import * as React from 'react';
import { connect } from 'react-redux';
import { flow, map, forEach } from 'lodash';
import { Col, Row, OverlayTrigger, Popover } from 'react-bootstrap';
import { II18nMessages } from '../../../../services/branding/models';
import { IDestination, IPolicyApplication, IPremium, IProduct, IQuotes, ITraveller } from '../../../../data/api/models';
import { Field, formValueSelector, submit } from 'redux-form';
import { IStore } from '../../../../redux/IStore';
import { FormattedNumber } from 'react-intl';
import { ICompleteData } from '../../../../services/quote/models';
import FaInfoCircle from 'react-icons/fa/info-circle';
import { showDestinations, showRegions } from '../../../../data/api/api';
import { bindActionCreators } from 'redux';
import { actions } from '../../../../services/quote/reducer';
import { push } from 'react-router-redux';
import FieldFormControl from '../../../../components/FieldFormControl/index';
import { isRequired } from '../../../../util/validators';

interface IQuoteSummaryProps {
  i18n: II18nMessages;
  policyApplication: IPolicyApplication;
  destinations: IDestination[];
  values: IPolicyApplication;
  product: IProduct;
  quotes: IQuotes;
  complete: ICompleteData;
  actions: {
    submit(name: string);
    push: (route: string) => void;
    calculate: any;
  };
}

class QuoteSummary extends React.Component<IQuoteSummaryProps, any> {
  private renderQuoteReference = () => {
    const { values } = this.props;

    return (
      <div className="section">
        <Row>
          <Col sm={4}>
            Quote Reference
          </Col>
          <Col sm={8}>
            <div className="quote-info">
              {values.quoteReference}
            </div>
          </Col>
        </Row>
      </div>
    );
  }
  private renderTripType = () => {
    const { i18n, values, product, quotes } = this.props;

    const coverLevel = Object.keys(quotes)[0];

    return (
      <div className="section">
        <Row>
          <Col sm={4}>
            {i18n.quote.summary.tripType}
          </Col>
          <Col sm={8}>
            <div className="quote-info">
              {product.metadata.trip_type[values.tripType]} - <span className="text-capitalize">{coverLevel}</span>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  private renderTravelDate = () => {
    const { values } = this.props;

    let content = (
      <p>No dates entered</p>
    );

    if (values.startDate) {
      content = (
        <div>
          {values.startDate}
          {values.tripType === 'single' && values.endDate ? ' - ' + values.endDate : ''}
        </div>
      );
    }

    return (
      <div className="section">
        <Row>
          <Col sm={4}>
            {values.tripType === 'single' ? 'Travel Dates' : 'Start Date'}
          </Col>
          <Col sm={8}>
            <div className="quote-info">
              {content}
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  private renderDestination = () => {
    const { values, product } = this.props;

    let content = (
      <div>Nothing Selected</div>
    );

    if (showDestinations(values.tripType) && values.destinations.length > 0 && values.destinations[0].length > 0) {
      content = (
        <div>
          {values.destinations.map((destination, key) => (<div key={key}>{destination}</div>))}
        </div>
      );
    }

    // if (showRegions(values.tripType) && values.region) {
    if (values.region) {
      content = (
        <div>{product.metadata.regions[ values.region ]}</div>
      );
    }

    return (
      <div className="section">
        <Row>
          <Col sm={4}>
            {showDestinations(values.tripType) ?
              (values.destinations.length > 1 ? 'Destinations' : 'Destination') : 'Region'}
          </Col>
          <Col sm={8}>
            <div className="quote-info">
              {content}
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  private renderAdditionalOptions = () => {
    const { values, product } = this.props;

    const options = map(values.options, (value: any, key) => {
      const productOption = product.metadata.options[ key ];

      if (productOption && productOption.type === 'boolean' || 'question' && value === true) {
        return (
          <div key={key}>{productOption.name}</div>
        );
      }

      if (productOption && productOption.type === 'list') {
        const item = productOption.items[ value ];

        const popover = (
          <Popover
            id={`popover-summary-${name}`}
          >
            {item}
          </Popover>
        );

        const info = (
          <OverlayTrigger
            trigger="click"
            rootClose={true}
            overlay={popover}
            placement="right"
            positionLeft={200}
            positionTop={50}
          >
            <FaInfoCircle className="pointer" />
          </OverlayTrigger>
        );

        return (
          <div key={key}>
            {productOption.name} {info}
          </div>
        );
      }
    }).filter((option) => !!option);

    if (options.length === 0) {
      return null;
    }

    // const renderOptions = options.map((option, key) => {
    //   return (<p key={key}>{option}</p>);
    // });

    return (
      <div className="section">
        <Row>
          <Col sm={4}>
            Additional {options.length > 1 ? 'Options' : 'Option'}
          </Col>
          <Col sm={8}>
            <div className="quote-info">
              {options}
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  private renderTraveller = (type: string, display: string) => (traveller: ITraveller, key) => {
    let conditions = null;

    if (traveller.screening) {
      conditions = (
        <span className="traveller-conditions"><br />{traveller.screening.meta.conditions.join(', ')}</span>
      );
    }

    return (
      <div key={key} className="sessioncamhidetext">
        {display} {key + 1}: <em>{traveller.title} {traveller.firstName} {traveller.lastName}</em> ({traveller.dob})
        {conditions}
      </div>
    );
  }

  private renderTravellers = () => {
    const { values, product } = this.props;
    const { travellers } = values;

    if (!travellers || !values.tripType) {
      return null;
    }

    const hasNoTravellerDeclaredCondition = !travellers.some((traveller) => {
      return traveller.screening;
    });

    const adults = travellers.filter((traveller) => traveller.type === 'adult');
    const children = travellers.filter((traveller) => traveller.type === 'child');
    const groupTypes = product.metadata[ `group_type_trip_${values.tripType}` ];

    const total = adults.length + children.length;

    let summaryMessage = `${adults.length} ${adults.length > 1 ? 'adults' : 'adult'}`;

    if (children.length) {
      summaryMessage += `, ${children.length} ${children.length > 1 ? 'children' : 'child'}`;
    }

    return (
      <div className="section">
        <Row>
          <Col sm={4}>
            {total > 1 ? 'Travellers' : 'Traveller'}
          </Col>
          <Col sm={8}>
            <div className="quote-info">
              <div>{groupTypes[ values.groupType ]} ({summaryMessage})</div>

              {adults.map(this.renderTraveller('adult', 'Adult'))}
              {children.map(this.renderTraveller('child', 'Child'))}
            </div>
          </Col>
        </Row>
        {hasNoTravellerDeclaredCondition && (
          <div className="important">
            You have confirmed that no traveller covered by this policy has any medical
            conditions that needed to be declared to us.
          </div>
        )}
      </div>
    );
  }

  private renderPremium = () => {
    const { quotes, values : { schemeId }, product, values } = this.props;

    if (! quotes) {
      return null;
    }

    let premium: IPremium = null;

    forEach(quotes, (tripTypes, level) => {
      forEach(tripTypes, (quotedPremium) => {
        if (!quotedPremium) {
          return null;
        }

        if (quotedPremium.scheme.id === schemeId) {
          premium = quotedPremium;
        }
      });
    });

    if (! premium) {
      return null;
    }

    return (
      <div className="white-section">
        <Row>
          <Col sm={4}>
            {/*Premium (inc {product.metadata.tax_type})*/}
            <p>Total price to pay</p>
          </Col>
          <Col sm={8}>
            <span className="quote-info">
              <p>
                <FormattedNumber
                  value={premium.gross}
                  style="currency"
                  currency={premium.currency}
                />
              </p>
            </span>
          </Col>
        </Row>
      </div>
    );
  }

  private renderPolicyAgreement = () => {
    const { quotes, values : { schemeId } } = this.props;

    let premium: IPremium = null;

    forEach(quotes, (tripTypes, level) => {
      forEach(tripTypes, (quotedPremium) => {
        if (!quotedPremium) {
          return null;
        }

        if (quotedPremium.scheme.id === schemeId) {
          premium = quotedPremium;
        }
      });
    });

    return (
      <div className="section">
        <Field
          name="policyDeclaration"
          type="checkbox"
          value="true"
          component={FieldFormControl}
          validate={[isRequired]}
        >
          <div />
          Please confirm you have read our
          {' '}<a href={premium && premium.scheme.documents['Policy Wording']} target="_blank">Policy Wording</a> and
          {' '}<a href={premium && premium.scheme.documents['Policy Summary']} target="_blank">Policy Summary</a>.
        </Field>
        <div className="important margin-top">
          <strong>IMPORTANT</strong> - If you are unable to confirm this statement you will
          be unable to continue with this quote.
        </div>
      </div>
    );
  }

  public render() {
    const { values, product, complete } = this.props;

    if (! values || ! product) {
      return null;
    }

    return (
      <div>
        <Row>
          <Col sm={12}>
            <div className="section-title">
              <h2>{complete ? 'Your Policy Summary' : 'Your Quote Summary'}</h2>
            </div>
            <div className="section-group">
              {this.renderQuoteReference()}
              {this.renderTripType()}
              {this.renderTravelDate()}
              {this.renderDestination()}
              {this.renderAdditionalOptions()}
              {this.renderTravellers()}
              {this.renderPolicyAgreement()}
              {this.renderPremium()}
            </div>

            {/* TODO: Render Save Quote */}
          </Col>
        </Row>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const { quotes, complete } = state.quote;
      const values = selector(
        state,
        'destinations',
        'startDate',
        'endDate',
        'region',
        'tripType',
        'options',
        'travellers',
        'groupType',
        'schemeId',
        'quoteReference',
      );

      return {
        i18n: state.branding.i18n,
        policyApplication: state.quote.application,
        destinations: state.quote.destinations,
        values,
        quotes,
        complete,
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
      },
    }),
  ),
])(QuoteSummary);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/QuoteSummary/index.tsx